import React, { useEffect, useMemo, useRef, useState } from "react";

const formatTime = () => {
  const d = new Date();
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const uid = () => Math.random().toString(16).slice(2);

const WHATSAPP_URL = "https://wa.me/573142035583?text=Hola%20Yerika,%20me%20gustaría%20hablar%20sobre%20un%20proyecto.";
const EMAIL_TO = "marleyrozo@gmail.com";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [mode, setMode] = useState("menu"); // "menu" or "lead"
  const [lead, setLead] = useState({ name: "", email: "", message: "" });
  const [isTyping, setIsTyping] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  const listRef = useRef(null);

  const quick = useMemo(
    () => [
      { key: "write", label: "Escribirme un mensaje", icon: "✉️" },
      { key: "exp", label: "Experiencia", icon: "💼" },
      { key: "skills", label: "Habilidades", icon: "🛠️" },
      { key: "projects", label: "Proyectos", icon: "🚀" },
      { key: "contact", label: "Contacto directo", icon: "📞" },
    ],
    []
  );

  const [messages, setMessages] = useState([
    {
      id: uid(),
      from: "bot",
      text: "¡Hola! Soy Yerika, tu asistente virtual. ¿En qué puedo ayudarte hoy?",
      time: formatTime(),
    },
  ]);

  // Scroll al último mensaje
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (listRef.current) {
        listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
      }
    }, 100);
    return () => clearTimeout(t);
  }, [open, messages.length, isTyping]);

  // Notificación de nuevo mensaje cuando el chat está cerrado
  useEffect(() => {
    if (messages.length > 1 && !open) {
      setHasNewMessage(true);
    }
  }, [messages.length, open]);

  const pushBot = (text) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages((m) => [...m, { id: uid(), from: "bot", text, time: formatTime() }]);
      setIsTyping(false);
    }, 600 + Math.min(text.length * 15, 1500));
  };

  const pushUser = (text) => {
    setMessages((m) => [...m, { id: uid(), from: "user", text, time: formatTime() }]);
    setHasNewMessage(false);
  };

  const reset = () => {
    setMode("menu");
    setLead({ name: "", email: "", message: "" });
    setDraft("");
    setMessages([
      { 
        id: uid(), 
        from: "bot", 
        text: "¡Hola de nuevo! ¿Qué te gustaría conocer sobre mí o mi trabajo?", 
        time: formatTime() 
      },
    ]);
  };

  const handleQuick = (key) => {
    const picked = quick.find((q) => q.key === key)?.label || key;
    pushUser(picked);

    setTimeout(() => {
      switch (key) {
        case "exp":
          pushBot(
            "Tengo más de 2 años de experiencia en desarrollo web full-stack. Especializada en Angular, Tailwind CSS, y backend con Python."
          );
          break;
        case "skills":
          pushBot(
            "Frontend: Angular, TypeScript, React, Tailwind\n" +
            "Backend: Python, Flask, FastAPI\n" +
            "Cloud: AWS, Firebase, CI/CD"
          );
          break;
        case "projects":
          pushBot(
            "He trabajado en plataformas de e-commerce, sistemas logísticos y dashboards analíticos."
          );
          break;
        case "contact":
          pushBot("¡Excelente! Puedes contactarme por WhatsApp o correo electrónico. ¿Cuál prefieres?");
          break;
        case "write":
          setMode("lead");
          pushBot("Perfecto, me encantaría saber más sobre tu proyecto. ¿Cuál es tu nombre?");
          break;
        default:
          pushBot("¿Te gustaría conocer mi experiencia, habilidades, proyectos o contactarme?");
      }
    }, 300);
  };

  const submitText = () => {
    const text = draft.trim();
    if (!text) return;
    pushUser(text);
    setDraft("");

    setTimeout(() => {
      if (mode === "lead") {
        if (!lead.name) {
          setLead((s) => ({ ...s, name: text }));
          pushBot(`¡Hola ${text}! ¿Me podrías compartir tu email?`);
          return;
        }
        if (!lead.email) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(text)) {
            pushBot("El email no parece correcto. ¿Podrías escribirlo de nuevo?");
            return;
          }
          setLead((s) => ({ ...s, email: text }));
          pushBot("✅ Email guardado. ¿Podrías contarme brevemente tu proyecto?");
          return;
        }
        if (!lead.message) {
          setLead((s) => ({ ...s, message: text }));
          pushBot("¡Gracias! ¿Prefieres enviar por correo o WhatsApp?");
          return;
        }
      }

      const lower = text.toLowerCase();
      if (lower.includes("whatsapp") || lower.includes("wsp")) {
        pushBot("Perfecto, te redirijo a WhatsApp ahora mismo.");
        setTimeout(() => {
          window.open(WHATSAPP_URL, '_blank');
        }, 1000);
        return;
      }
      
      if (lower.includes("correo") || lower.includes("email")) {
        pushBot("Listo, se abrirá tu cliente de correo.");
        return;
      }

      if (lower.includes("hola") || lower.includes("hi")) {
        pushBot("¡Hola! ¿En qué puedo ayudarte?");
        return;
      }

      pushBot("¿Te gustaría conocer mi experiencia, habilidades, proyectos o contactarme?");
    }, 400);
  };

  const mailtoHref = useMemo(() => {
    const subject = `Contacto desde portafolio: ${lead.name || "Interesado"}`;
    const body =
      `Nombre: ${lead.name || "No proporcionado"}\n` +
      `Email: ${lead.email || "No proporcionado"}\n` +
      `Mensaje:\n${lead.message || "No proporcionado"}`;
    return `mailto:${EMAIL_TO}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [lead]);

  const currentLeadStep = useMemo(() => {
    if (!lead.name) return 1;
    if (!lead.email) return 2;
    if (!lead.message) return 3;
    return 4;
  }, [lead]);

  const handleOpenChat = () => {
    setOpen(true);
    setHasNewMessage(false);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 font-sans">
      <button
        onClick={handleOpenChat}
        aria-label={open ? "Cerrar chat" : "Abrir chat"}
        className="group relative h-16 w-16 rounded-full bg-gradient-to-br from-[#3f5232] to-[#2a3a1f] shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-[#3f5232]/40"
      >
        <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#4a653a] to-[#3f5232] blur-xl opacity-50 group-hover:opacity-70 transition-opacity duration-300" />
        
        <span className="absolute inset-0 rounded-full ring-2 ring-white/20 group-hover:ring-white/30 transition" />
        
        {hasNewMessage && (
          <span className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center animate-pulse ring-2 ring-white">
            {messages.length - 1}
          </span>
        )}
        
        <span className="relative grid place-items-center h-16 w-16 rounded-full">
          <span className="absolute h-9 w-9 rounded-full bg-white/20 group-hover:bg-white/25 transition-colors duration-300" />
          <svg
            className="relative h-7 w-7 text-white transform group-hover:scale-110 transition-transform duration-300"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M7 8h10M7 12h6m1 8l2.35-4.65A7.8 7.8 0 0020 12c0-4.418-3.582-8-8-8s-8 3.582-8 8 3.582 8 8 8c1.65 0 3.2-.5 4.5-1.35L19 20z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-20 sm:right-4 z-50 flex items-end sm:items-start justify-center sm:justify-end p-2 sm:p-0">
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-sm sm:hidden"
            onClick={() => setOpen(false)}
          />
          
          <div className={`
            w-full sm:w-[420px]
            h-[85vh] sm:h-[640px]
            max-h-[700px] sm:max-h-[80vh]
            bg-gradient-to-b from-white to-[#f9faf8]
            rounded-2xl sm:rounded-3xl
            border border-[#3f5232]/20
            shadow-2xl shadow-black/15
            flex flex-col
            transform transition-all duration-300 ease-out
            ${open ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 sm:translate-y-0'}
          `}>
            <div className="flex-none h-16 px-5 flex items-center justify-between bg-gradient-to-r from-[#3f5232] to-[#2a3a1f] rounded-t-2xl sm:rounded-t-3xl">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-white/20 to-white/10 ring-2 ring-white/20 grid place-items-center relative">
                  <div className="h-3 w-3 rounded-full bg-emerald-400 animate-pulse" />
                  <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center">
                    <span className="text-xs text-white">✓</span>
                  </div>
                </div>
                <div className="leading-tight">
                  <div className="text-white font-bold text-sm">Asistente de Yerika</div>
                  <div className="text-white/90 text-xs flex items-center gap-2">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    En línea • Responde en segundos
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={reset}
                  className="text-white/90 hover:text-white text-xs font-semibold px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/30"
                  title="Reiniciar conversación"
                >
                  Reiniciar
                </button>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Cerrar chat"
                  className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/15 transition-all duration-200 text-white grid place-items-center focus:outline-none focus:ring-2 focus:ring-white/30"
                >
                  <span className="text-lg leading-none font-light">×</span>
                </button>
              </div>
            </div>

            {mode === "lead" && (
              <div className="flex-none px-5 py-3 bg-gradient-to-r from-[#3f5232]/5 to-white/50 border-b border-[#3f5232]/10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-[#3f5232]">Completando formulario</span>
                  <span className="text-xs text-slate-500">{currentLeadStep}/4</span>
                </div>
                <div className="flex items-center gap-1">
                  {['Nombre', 'Email', 'Mensaje', 'Enviar'].map((step, idx) => {
                    const stepNumber = idx + 1;
                    return (
                      <div key={step} className="flex-1 flex items-center">
                        <div className={`
                          h-2 rounded-full w-full transition-all duration-500
                          ${currentLeadStep >= stepNumber ? 'bg-[#3f5232]' : 'bg-slate-200'}
                        `} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div
              ref={listRef}
              className="flex-1 px-5 py-4 overflow-y-auto bg-gradient-to-b from-white via-white to-[#f9faf8]"
            >
              <div className="space-y-4">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"} group`}>
                    {m.from === "bot" && (
                      <div className="flex-shrink-0 h-9 w-9 mt-1 mr-3 rounded-full bg-gradient-to-br from-[#3f5232] to-[#2a3a1f] flex items-center justify-center text-white text-sm font-bold shadow-md ring-2 ring-white/20">
                        Y
                      </div>
                    )}
                    <div className={`max-w-[85%] ${m.from === "user" ? 'order-first' : ''}`}>
                      <div className={`
                        px-4 py-3 text-sm leading-relaxed whitespace-pre-line
                        transition-all duration-200 transform
                        ${m.from === "user"
                          ? "bg-gradient-to-br from-[#3f5232] to-[#2a3a1f] text-white rounded-2xl rounded-br-sm shadow-md hover:shadow-lg"
                          : "bg-white text-slate-800 border border-slate-200/80 rounded-2xl rounded-bl-sm shadow-sm hover:shadow-md hover:border-[#3f5232]/30"}
                        group-hover:scale-[1.02]
                      `}>
                        {m.text}
                      </div>
                      <div className={`mt-1.5 text-[11px] text-slate-500 px-1 ${m.from === "user" ? "text-right" : "text-left"}`}>
                        {m.time}
                      </div>
                    </div>
                    {m.from === "user" && (
                      <div className="flex-shrink-0 h-9 w-9 mt-1 ml-3 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-white text-sm font-bold shadow-md ring-2 ring-white/20">
                        T
                      </div>
                    )}
                  </div>
                ))}

                {isTyping && (
                  <div className="flex justify-start">
                    <div className="flex-shrink-0 h-9 w-9 mt-1 mr-3 rounded-full bg-gradient-to-br from-[#3f5232] to-[#2a3a1f] flex items-center justify-center text-white text-sm font-bold shadow-md">
                      Y
                    </div>
                    <div className="px-4 py-3 bg-white border border-slate-200/80 rounded-2xl rounded-bl-sm shadow-sm">
                      <div className="flex items-center space-x-1.5">
                        <div className="h-2 w-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="h-2 w-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="h-2 w-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {mode === "menu" && messages.length <= 5 && (
                <div className="mt-6">
                  <div className="text-xs font-semibold text-slate-500 mb-3 px-1">OPCIONES RÁPIDAS</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {quick.map((q) => (
                      <button
                        key={q.key}
                        onClick={() => handleQuick(q.key)}
                        className="
                          px-4 py-3
                          rounded-xl
                          bg-white
                          border border-slate-200/80
                          text-sm font-medium text-slate-800
                          shadow-sm
                          hover:bg-gradient-to-r hover:from-[#3f5232]/5 hover:to-[#3f5232]/10
                          hover:border-[#3f5232]/40
                          hover:text-[#3f5232]
                          hover:shadow
                          active:scale-[0.98]
                          transition-all duration-200
                          focus:outline-none focus:ring-2 focus:ring-[#3f5232]/30 focus:border-transparent
                          flex items-center gap-2
                          group
                        "
                      >
                        <span className="text-base">{q.icon}</span>
                        <span className="text-left">{q.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {lead.name && lead.email && lead.message && (
                <div className="mt-6">
                  <div className="text-xs font-semibold text-slate-500 mb-3 px-1">ENVIAR MENSAJE</div>
                  <div className="grid grid-cols-1 gap-3">
                    <a
                      href={mailtoHref}
                      onClick={() => setMode("menu")}
                      className="rounded-xl bg-gradient-to-r from-[#3f5232] to-[#2a3a1f] text-white px-5 py-3.5 text-sm font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-[#3f5232]/40 flex items-center justify-between group"
                    >
                      <span>Enviar por correo</span>
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity">✉️</span>
                    </a>
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMode("menu")}
                      className="rounded-xl bg-white text-slate-800 px-5 py-3.5 text-sm font-semibold border-2 border-[#3f5232]/20 shadow-lg hover:shadow-xl hover:scale-[1.02] hover:border-[#3f5232]/40 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-[#3f5232]/20 flex items-center justify-between group"
                    >
                      <span>Enviar por WhatsApp</span>
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity">💬</span>
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-none h-16 px-4 border-t border-slate-200/70 bg-white/95 flex items-center gap-2">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submitText();
                  }
                }}
                className="
                  flex-1
                  h-11
                  rounded-full
                  border border-slate-300
                  bg-white
                  px-5
                  text-sm text-slate-900
                  placeholder:text-slate-400
                  focus:outline-none
                  focus:ring-4 focus:ring-[#3f5232]/20
                  focus:border-[#3f5232]/40
                  transition-all duration-200
                  shadow-inner
                "
                placeholder={mode === "lead" ? 
                  (currentLeadStep === 1 ? "Escribe tu nombre..." : 
                   currentLeadStep === 2 ? "nombre@ejemplo.com..." : 
                   "Describe tu proyecto o consulta...") : 
                  "Escribe tu mensaje aquí..."}
                disabled={isTyping}
              />
              <button
                onClick={submitText}
                disabled={!draft.trim() || isTyping}
                aria-label="Enviar mensaje"
                className={`
                  h-11 w-11
                  rounded-full
                  grid place-items-center
                  shadow-md
                  transition-all duration-200
                  focus:outline-none focus:ring-4
                  ${draft.trim() && !isTyping
                    ? 'bg-gradient-to-br from-[#3f5232] to-[#2a3a1f] text-white hover:shadow-lg hover:scale-105 focus:ring-[#3f5232]/40'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'}
                `}
              >
                {isTyping ? (
                  <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none">
                    <path
                      d="M4 12l16-8-6 16-2-6-8-2z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>

            <div className="flex-none px-4 py-2.5 border-t border-slate-200/50 bg-white/80 text-center">
              <div className="text-[10px] text-slate-500">
                Asistente virtual de <span className="font-semibold text-[#3f5232]">Yerika Rozo</span> • 
                Respuestas en tiempo real • <button onClick={reset} className="text-[#3f5232] hover:underline">Reiniciar chat</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}